import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  createPasswordRecoveryFormSchema,
  type SendPasswordRecoveryCodeResponseDto,
  usePasswordRecoveryConfirmation,
} from '@/features/auth-login';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { DEFAULT_OTP_CODE_LENGTH } from '@/shared/config';

type PasswordRecoveryFormValues = z.infer<
  ReturnType<typeof createPasswordRecoveryFormSchema>
>;

const usePasswordRecoveryForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const schema = createPasswordRecoveryFormSchema(t);
  const form = useForm<PasswordRecoveryFormValues>({
    mode: 'onSubmit',
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
    },
  });

  useEffect(() => {
    if (form.formState.isSubmitted) {
      form.trigger();
    }
  }, [t]);

  const { sendRecoveryCode, isSubmitting } = usePasswordRecoveryConfirmation({
    onSuccess: (data: SendPasswordRecoveryCodeResponseDto) => {
      if (data.success) {
        const email = form.getValues('email');

        navigate('/login/password-recovery-confirmation', {
          state: {
            email,
            state: email,
            codeData: {
              length: DEFAULT_OTP_CODE_LENGTH,
            },
          },
        });
      }
    },
  });

  const submitHandler = async (formValues: PasswordRecoveryFormValues) => {
    try {
      await sendRecoveryCode(formValues.email);
    } catch (error) {
      console.log(error);
    }
  };

  return {
    form,
    submitHandler,
    isSubmitting,
  };
};

export { usePasswordRecoveryForm };
